import { supabase } from '../lib/supabaseClient';
import { auth$ } from './auth$';
import { notes$ } from './notes$';
import { trackEvent } from '../lib/analytics';

/**
 * Share a note - creates entry in promptnote_shared_notes
 * Returns { id, shareToken } on success
 */
export const shareNote = async (noteId) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  // Get note details for metadata
  const note = notes$[noteId]?.get();
  if (!note) throw new Error('Note not found');

  const { data, error } = await supabase
    .from('promptnote_shared_notes')
    .insert({
      note_id: noteId,
      user_id: user.id,
      note_title: note.title || 'Untitled Note',
      share_mode: 'live',
    })
    .select('id, share_token')
    .single();

  if (error) throw error;

  return {
    id: data.id,
    shareToken: data.share_token,
  };
};

/**
 * Get shared note by token (for public viewing)
 * Uses the view that joins with original note and snippets for live content
 */
export const getSharedNoteByToken = async (shareToken) => {
  if (!shareToken) return null;

  const { data, error } = await supabase
    .from('promptnote_shared_notes_with_content')
    .select('*')
    .eq('share_token', shareToken)
    .eq('is_revoked', false)
    .single();

  if (error) {
    console.error('Failed to fetch shared note:', error);
    return null;
  }

  return data;
};

/**
 * Increment view count (fire-and-forget)
 */
export const incrementViewCount = async (shareToken) => {
  if (!shareToken) return;

  try {
    await supabase.rpc('increment_promptnote_shared_note_view_count', {
      p_share_token: shareToken,
    });
  } catch (error) {
    console.warn('Failed to increment view count:', error);
  }
};

/**
 * Increment snippet copy count via RPC (fire-and-forget)
 * Works for anonymous users on shared note view
 */
export const incrementSnippetCopyCount = async (snippetId) => {
  if (!snippetId) return;

  try {
    await supabase.rpc('increment_snippet_copy_count', {
      p_snippet_id: snippetId,
    });
  } catch (error) {
    console.warn('Failed to increment snippet copy count:', error);
  }
};

/**
 * Get existing share for a note (if already shared)
 */
export const getExistingShare = async (noteId) => {
  const user = auth$.user.get();
  if (!user) return null;

  const { data, error } = await supabase
    .from('promptnote_shared_notes')
    .select('id, share_token, is_revoked, view_count, created_at')
    .eq('note_id', noteId)
    .eq('user_id', user.id)
    .eq('is_revoked', false)
    .maybeSingle();

  if (error) {
    console.error('Failed to check existing share:', error);
    return null;
  }

  return data;
};

/**
 * Revoke a shared note - sets is_revoked to true
 */
export const revokeShare = async (shareToken) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('promptnote_shared_notes')
    .update({
      is_revoked: true,
      revoked_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('share_token', shareToken)
    .eq('user_id', user.id);

  if (error) throw error;
};

/**
 * Get all shares for current user (for future management UI)
 */
export const getUserShares = async () => {
  const user = auth$.user.get();
  if (!user) return [];

  const { data, error } = await supabase
    .from('promptnote_shared_notes')
    .select('id, share_token, note_id, note_title, is_revoked, view_count, created_at')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch user shares:', error);
    return [];
  }

  return data;
};

/**
 * Report shared content (for UGC compliance)
 * Works for both authenticated and anonymous users
 */
export const reportContent = async ({ contentType, contentId, reason, details }) => {
  const user = auth$.user.get();

  const { error } = await supabase
    .from('content_reports')
    .insert({
      reporter_id: user?.id || null,
      content_type: contentType,
      content_id: contentId,
      reason,
      details: details || null,
    });

  if (error) throw error;

  trackEvent('content_reported', { content_type: contentType, reason });
};
